import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { SlidersHorizontal } from 'lucide-react';
interface SlideCalculatorProps {
  onContinue: () => void;
}
export function SlideCalculator({
  onContinue
}: SlideCalculatorProps) {
  const [revenueB, setRevenueB] = useState<number>(0);
  const [legacyPercent, setLegacyPercent] = useState<number>(0);
  const [improvementPercent, setImprovementPercent] = useState<number>(0);
  const [animationDone, setAnimationDone] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  useEffect(() => {
    const t0 = setTimeout(() => setRevenueB(45), 600);
    const t1 = setTimeout(() => setLegacyPercent(35), 1400);
    const t2 = setTimeout(() => setImprovementPercent(1), 2200);
    const t3 = setTimeout(() => setAnimationDone(true), 3200);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  const calculateUpside = () => revenueB * 1000 * (legacyPercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 800
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    upsideValue.set(calculateUpside());
  }, [revenueB, legacyPercent, improvementPercent]);
  const handleUserChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInteracted(true);
    setter(Number(e.target.value));
  };
  return <div className="h-full w-full flex flex-col px-12 py-12 md:px-24 md:py-16 max-w-7xl mx-auto" data-id="element-145">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-white mb-8" data-id="element-146">
        Even a <span className="text-teal-400" data-id="element-147">{improvementPercent}%</span>{' '}
        improvement across the legacy portfolio =
      </motion.h2>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center" data-id="element-148">
        <div className="lg:col-span-5 space-y-7 bg-navy-900/70 p-8 rounded-3xl border border-navy-600 relative" data-id="element-149">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone && !userInteracted ? 1 : 0
        }} className="absolute -top-12 left-0 right-0 flex items-center justify-center gap-2" data-id="element-150">
            <motion.div animate={{
            y: [0, -4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.5
          }} className="flex items-center gap-2 bg-teal-400/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium border border-teal-400/30" data-id="element-151">
              <SlidersHorizontal className="w-4 h-4" data-id="element-152" />
              Adjust the sliders — set your own numbers
            </motion.div>
          </motion.div>

          <div className="space-y-3" data-id="element-153">
            <div className="flex justify-between items-end" data-id="element-154">
              <label className="text-white/80 font-medium" data-id="element-155">
                Pharma Division Revenue
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-156">
                €{revenueB}B
              </span>
            </div>
            <input type="range" min="5" max="80" step="1" value={revenueB} onChange={handleUserChange(setRevenueB)} className="w-full" data-id="element-157" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-158">
              <span data-id="element-159">€5B</span>
              <span className="text-teal-400/60" data-id="element-160">Roche Pharma: ~€45B</span>
              <span data-id="element-161">€80B</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-162">
            <div className="flex justify-between items-end" data-id="element-163">
              <label className="text-white/80 font-medium" data-id="element-164">
                % from Legacy / Off-Patent Brands
              </label>
              <span className="text-2xl font-serif text-white" data-id="element-165">
                €{(revenueB * legacyPercent / 100).toFixed(1)}B
              </span>
            </div>
            <input type="range" min="10" max="80" step="5" value={legacyPercent} onChange={handleUserChange(setLegacyPercent)} className="w-full" data-id="element-166" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-167">
              <span data-id="element-168">10%</span>
              <span className="text-teal-400/60" data-id="element-169">{legacyPercent}%</span>
              <span data-id="element-170">80%</span>
            </div>
          </div>

          <div className="space-y-3" data-id="element-171">
            <div className="flex justify-between items-end" data-id="element-172">
              <label className="text-white/80 font-medium" data-id="element-173">
                Target Improvement
              </label>
              <span className="text-2xl font-serif text-teal-400" data-id="element-174">
                {improvementPercent}%
              </span>
            </div>
            <input type="range" min="1" max="20" step="1" value={improvementPercent} onChange={handleUserChange(setImprovementPercent)} className="w-full" data-id="element-175" />
            <div className="flex justify-between text-xs text-white/40" data-id="element-176">
              <span data-id="element-177">1%</span>
              <span data-id="element-178">20%</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col items-center lg:items-start justify-center text-center lg:text-left" data-id="element-179">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.2
        }} className="space-y-4" data-id="element-180">
            <h3 className="text-2xl text-white/60 font-medium uppercase tracking-widest" data-id="element-181">
              Unlocked Value
            </h3>
            <div className="flex items-baseline gap-2" data-id="element-182">
              <span className="text-6xl md:text-8xl lg:text-9xl font-serif text-teal-400 tracking-tight" data-id="element-183">
                €
              </span>
              <motion.span className="text-6xl md:text-8xl lg:text-9xl font-serif text-teal-400 tracking-tight" data-id="element-184">
                {displayUpside}
              </motion.span>
              <span className="text-4xl md:text-6xl font-serif text-teal-400" data-id="element-185">
                M
              </span>
            </div>
            <p className="text-xl text-white/70 mt-6 border-l-2 border-teal-400/40 pl-4 py-2" data-id="element-186">
              This value is currently unmanaged and under-optimized.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: animationDone ? 1 : 0
        }} transition={{
          delay: 0.5
        }} className="mt-12" data-id="element-187">
            <Button onClick={onContinue} className="text-lg px-8 py-4" data-id="element-188">
              AI Economics
            </Button>
          </motion.div>
        </div>
      </div>
    </div>;
}